/* eslint-disable react-hooks/rules-of-hooks */
import { GoogleTagManager } from '@next/third-parties/google'
import swrConfig from 'config/swrConfig'
import { NextComponentType } from 'next'
import { AppProps } from 'next/app'
import Head from 'next/head'
import { IntlProvider, MissingTranslationError, useIntl } from 'react-intl'
import { SWRConfig } from 'swr'

import { BasicLayout } from '@/components/layout/BasicLayout'
import { ErrorBoundary } from '@/components/layout/ErrorBoundary'
import { WrongWorkspace } from '@/components/layout/WrongWorkspace/WrongWorkspace'
import CustomIntercomProvider from '@/components/ui/intercom/CustomIntercomProvider'
import { ENVIRONMENT, INTERCOM_APP_ID } from '@/config/ui'
import { LeavePreventionProvider } from '@/context/providers/LeavePreventionProvider'
import { NotificationProvider } from '@/context/providers/NotificationProvider'
import { PaywallProvider } from '@/context/providers/PaywallProvider'
import { ThemeProvider } from '@/context/providers/ThemeProvider'
import { TourProvider } from '@/context/providers/TourProvider'
import { WorkspaceProvider } from '@/context/providers/WorkspaceProvider'
import { useAuth } from '@/hooks/useAuth'
import { useRaygun } from '@/hooks/useRaygun'
import messages from '@/locales/en_US'
import '@/styles/global.css'
import { buildI18nFunction } from '@/utils/messages'

import { Workspace } from '@/types/index.d'

type PageWithMetadata = NextComponentType & {
  metadata: PageMetadata
}

export type PageMetadata = {
  hasFullscreenLayout?: boolean
  isErrorPage?: boolean
  isPublic?: boolean
  isThemeLightMode?: boolean
  workspaceOverrideCriteria?: (workspaces: Workspace[]) => Workspace
}

function App({ Component, pageProps }: AppProps): JSX.Element {
  const pageMetadata = (Component as PageWithMetadata).metadata as PageMetadata
  if (pageMetadata?.isErrorPage) {
    return <Component {...pageProps} />
  }

  // This hook is performing the actual authentication
  const { data: user, isLoading: isLoadingUserData } = useAuth(pageMetadata)
  useRaygun(user)

  const shouldInitializeUserTracking =
    !(typeof window !== 'undefined' && window?.Cypress) &&
    !isLoadingUserData &&
    !user?.is_impersonating

  return (
    <SWRConfig value={swrConfig}>
      <IntlProvider
        defaultLocale="en"
        locale={
          (typeof navigator !== 'undefined' && navigator.language) || 'en'
        }
        messages={messages}
        onError={handleIntlErrors}
      >
        <ThemeProvider pageMetadata={pageMetadata}>
          <NotificationProvider>
            <LeavePreventionProvider>
              <CustomIntercomProvider
                appId={INTERCOM_APP_ID}
                pageMetadata={pageMetadata}
                shouldInitializeUserTracking={shouldInitializeUserTracking}
              >
                <Header />
                <WorkspaceProvider
                  pageMetadata={pageMetadata}
                  shouldInitializeAmplitude={shouldInitializeUserTracking}
                >
                  <TourProvider>
                    <PaywallProvider>
                      {renderPageBody(Component, pageMetadata, pageProps)}
                      {ENVIRONMENT.isProduction && (
                        <GoogleTagManager gtmId="GTM-M5XJZCRJ" />
                      )}
                    </PaywallProvider>
                  </TourProvider>
                </WorkspaceProvider>
              </CustomIntercomProvider>
            </LeavePreventionProvider>
          </NotificationProvider>
        </ThemeProvider>
      </IntlProvider>
    </SWRConfig>
  )
}

export default App

export function handleIntlErrors(_err: MissingTranslationError) {
  /**
   * This function is left blank because
   * it must suppress all console warning
   */
  return
}

function Header() {
  const t = buildI18nFunction(useIntl().formatMessage)
  return (
    <Head>
      <link rel="icon" href="/favicon.png" />
      <meta charSet="utf-8" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      <meta
        name="viewport"
        content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1,user-scalable=no"
      />
      <meta name="theme-color" content="#4038dd" />
      <title>{t(`Index.clayton.title`)}</title>
    </Head>
  )
}

function renderPageBody(Component, pageMetadata, pageProps) {
  if (pageMetadata?.hasFullscreenLayout) {
    return <Component {...pageProps} />
  }
  return (
    <BasicLayout baseUrl="/">
      <WrongWorkspace>
        <ErrorBoundary>
          <Component {...pageProps} />
        </ErrorBoundary>
      </WrongWorkspace>
    </BasicLayout>
  )
}
